import type * as React from 'react';
import { cn } from '@/lib/utils/cn';
import { ChartTooltip, useChartTooltip } from './chart-tooltip';
import {
  ChartEmpty,
  ChartLegend,
  ZONE_LABEL,
  ZONE_TINT_BG,
  cssVars,
  formatPct,
  zoneOf,
  type Thresholds,
} from './shared';
import { useEntryMotion } from './use-entry-motion';

/**
 * ScoreHeatMap — every criterion's score at each snapshot, then today
 * (prototype `heatmap()`): rows grouped by category, one cell per column
 * tinted by its gate zone (the only thing worked out here, a comparison
 * against the thresholds), today's column outlined, and the change since
 * the first column on the right (from the API). It is a real table, so it
 * is its own table view; each cell also says its zone in words for
 * screen readers.
 */
export interface HeatColumn {
  key: string;
  /** "6 Oct", "Today". */
  label: string;
  live?: boolean;
}

export interface HeatRow {
  id: string;
  name: string;
  /** Shown muted after the name ("8%"). */
  weight?: string | null;
  /** One value per column, 0..100, null when not scored then. */
  values: Array<number | null>;
  /** Points since the first column, from the API; null shows nothing. */
  change?: number | null;
}

export interface HeatGroup {
  label: string;
  rows: HeatRow[];
}

export interface ScoreHeatMapProps {
  columns: HeatColumn[];
  groups: HeatGroup[];
  thresholds: Thresholds;
  caption?: string;
  /** The criterion name cell, e.g. a router Link; plain text by default. */
  renderName?: (row: HeatRow) => React.ReactNode;
  motionKey?: string | number;
  className?: string;
}

export function ScoreHeatMap({
  columns,
  groups,
  thresholds,
  caption = 'Criteria scores at each snapshot',
  renderName,
  motionKey,
  className,
}: ScoreHeatMapProps) {
  const { animate, runKey } = useEntryMotion(motionKey);
  const { tip, bind } = useChartTooltip();

  if (!columns.length || !groups.some((g) => g.rows.length)) {
    return (
      <ChartEmpty title="No snapshots yet" className={className}>
        The criteria heat map starts with the first snapshot.
      </ChartEmpty>
    );
  }

  return (
    <>
      <table
        key={runKey}
        className={cn(
          'w-full border-separate border-spacing-0.5',
          animate && 'chart-anim',
          className,
        )}
      >
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            <th
              scope="col"
              className="w-[190px] p-1 text-left text-[11px] font-medium text-ink-500"
            >
              <span className="sr-only">Criterion</span>
            </th>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={cn(
                  'p-1 text-center text-[11px] font-medium',
                  c.live ? 'text-ink-900' : 'text-ink-500',
                )}
              >
                {c.label}
              </th>
            ))}
            <th scope="col" className="p-1 text-right text-[11px] font-medium text-ink-500">
              Change
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => [
            <tr key={`g-${g.label}`}>
              <td
                colSpan={columns.length + 2}
                className="px-0 pb-0.5 pt-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500"
              >
                {g.label}
              </td>
            </tr>,
            ...g.rows.map((r) => (
              <tr key={r.id}>
                <th
                  scope="row"
                  className="h-[30px] pl-0 text-left text-[13px] font-medium text-ink-900"
                >
                  {renderName ? renderName(r) : r.name}
                  {r.weight ? (
                    <span className="ml-1.5 text-[11px] font-normal text-ink-500">{r.weight}</span>
                  ) : null}
                </th>
                {columns.map((c, i) => {
                  const v = r.values[i] ?? null;
                  const zone = zoneOf(v, thresholds);
                  return (
                    <td
                      key={c.key}
                      className={cn(
                        'a-cell h-[30px] rounded text-center font-mono text-[12px] font-medium text-ink-900',
                        ZONE_TINT_BG[zone],
                        c.live && 'shadow-[inset_0_0_0_1.5px_var(--ink-700)]',
                      )}
                      style={cssVars({ '--i': i })}
                      {...bind({
                        title: `${r.name} · ${c.label}`,
                        value: v === null ? 'not scored' : formatPct(v),
                      })}
                    >
                      {v === null ? '—' : Math.round(v)}
                      <span className="sr-only">, {ZONE_LABEL[zone]}</span>
                    </td>
                  );
                })}
                <td
                  className={cn(
                    'pr-1 text-right font-mono text-[12px] font-semibold',
                    r.change && r.change > 0 && 'text-posture-healthy',
                    r.change && r.change < 0 && 'text-posture-critical',
                  )}
                >
                  {r.change === null || r.change === undefined
                    ? ''
                    : `${r.change > 0 ? '+' : ''}${Math.round(r.change)}`}
                </td>
              </tr>
            )),
          ])}
        </tbody>
      </table>
      <ChartTooltip tip={tip} />
    </>
  );
}

/** The heat map's key, with the gate values. */
export function HeatMapLegend({
  thresholds,
  className,
}: {
  thresholds: Thresholds;
  className?: string;
}) {
  return (
    <ChartLegend
      className={className}
      items={[
        { label: `At Go (${thresholds.go}+)`, color: 'bg-posture-healthy-tint' },
        {
          label: `Conditional (${thresholds.conditional}–${thresholds.go - 1})`,
          color: 'bg-posture-at-risk-tint',
        },
        { label: 'Below Conditional', color: 'bg-posture-critical-tint' },
      ]}
    />
  );
}
